import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import axios from 'axios';
import apiClient from '../../services/apiClient';
import gmapsIcon from '../../assets/maps/gmaps.svg';
import radarIcon from '../../assets/maps/radar.svg';
import uberIcon from '../../assets/maps/uber.svg';

export interface PodMarker {
    id: number;
    lat: number;
    lng: number;
    podUrl: string;
    trackingNumber?: string | null;
    createdAt: string;
}

interface ComparisonMapProps {
    googleCoordinates?: { lat: number; lng: number } | null;
    radarCoordinates?: { lat: number; lng: number } | null;
    uberCoordinates?: { lat: number; lng: number } | null;
    deliveredCoordinates?: { lat: number; lng: number } | null;
    currentCoordinates?: { lat: number; lng: number } | null;
    podMarkers?: PodMarker[];
    address?: string;
    height?: string;
    onCoordinateChange?: (lat: number, lng: number) => void;
    traceId?: string;
    initialMapType?: MapType;
    hideControls?: boolean;
    zoom?: number;
    googleLabel?: string;
    radarLabel?: string;
    uberLabel?: string;
    useColoredMarkers?: boolean;
    googleMarkerColor?: string;
    radarMarkerColor?: string;
    uberMarkerColor?: string;
}

type MapType = 'roadmap' | 'satellite' | 'hybrid' | 'terrain' | 'streetview';

const ComparisonMap: React.FC<ComparisonMapProps> = ({
    googleCoordinates,
    radarCoordinates,
    uberCoordinates,
    deliveredCoordinates,
    currentCoordinates,
    podMarkers = [],
    address,
    height = '300px',
    onCoordinateChange,
    traceId,
    initialMapType = 'satellite',
    hideControls = false,
    zoom: userZoom,
    googleLabel = 'Google',
    radarLabel = 'Radar',
    uberLabel = 'Uber',
    useColoredMarkers = false,
    googleMarkerColor = '#34a851',
    radarMarkerColor = '#000257',
    uberMarkerColor = '#000000'
}) => {
    const [mapType, setMapType] = useState<MapType>(initialMapType);
    const [isUploading, setIsUploading] = useState(false);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [showFullScreenPreview, setShowFullScreenPreview] = useState(false);
    const [showCropMode, setShowCropMode] = useState(false);
    const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
    const [cropZoom, setCropZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const fullScreenMapRef = useRef<HTMLDivElement>(null);

    // Define callbacks early before any early returns
    const handleOnCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const createCroppedImage = useCallback(async () => {
        if (!previewUrl || !croppedAreaPixels) {
            return;
        }

        try {
            const image = new Image();
            image.src = previewUrl;

            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = croppedAreaPixels.width;
                canvas.height = croppedAreaPixels.height;
                const ctx = canvas.getContext('2d');

                if (ctx) {
                    ctx.drawImage(
                        image,
                        croppedAreaPixels.x,
                        croppedAreaPixels.y,
                        croppedAreaPixels.width,
                        croppedAreaPixels.height,
                        0,
                        0,
                        croppedAreaPixels.width,
                        croppedAreaPixels.height
                    );
                }

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            toast.error('Failed to crop image');
                            return;
                        }

                        const croppedUrl = URL.createObjectURL(blob);
                        // Revoke old URL
                        if (previewUrl) {
                            URL.revokeObjectURL(previewUrl);
                        }
                        setPreviewBlob(blob);
                        setPreviewUrl(croppedUrl);
                        setShowCropMode(false);
                        toast.success('Image cropped successfully');
                    },
                    'image/png',
                    0.95
                );
            };
        } catch (error) {
            toast.error(
                `Failed to crop image: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
        }
    }, [previewUrl, croppedAreaPixels]);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    });

    if (loadError) {
        return (
            <div className="card">
                <div className="card-body text-center text-danger">
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                    <p className="mt-2">Error loading Google Maps</p>
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading map...</p>
                </div>
            </div>
        );
    }

    // If no coordinates available, show message
    if (!googleCoordinates && !radarCoordinates && !uberCoordinates && !currentCoordinates && !deliveredCoordinates && podMarkers.length === 0) {
        if (hideControls) {
            return (
                <div style={{ height: height, backgroundColor: '#f8f9fa' }} className="d-flex align-items-center justify-content-center text-muted border rounded">
                    <div className="text-center">
                        <i className="bi bi-geo-alt fs-4 opacity-50"></i>
                        <p className="m-0 small">No coordinates available</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="card shadow-sm">
                <div className="card-header bg-light fw-bold">Coordinate Comparison Map</div>
                <div className="card-body text-center py-5">
                    <i className="bi bi-geo-alt fs-1 text-muted opacity-50"></i>
                    <p className="mt-2 text-muted">No coordinate data available for mapping</p>
                    {address && <div className="small text-muted">{address}</div>}
                </div>
            </div>
        );
    }

    // Calculate center point between coordinates or use one if only one is available
    let center = { lat: 39.8283, lng: -98.5795 }; // Default to center of US
    let zoom = 4;

    const availableCoords = [
        googleCoordinates,
        radarCoordinates,
        uberCoordinates,
        currentCoordinates,
        deliveredCoordinates,
        ...podMarkers.map((pod) => ({ lat: pod.lat, lng: pod.lng }))
    ].filter(Boolean);

    if (availableCoords.length > 0) {
        if (availableCoords.length === 1) {
            center = { lat: availableCoords[0]!.lat, lng: availableCoords[0]!.lng };
        } else {
            // Calculate center from all available coordinates
            const avgLat = availableCoords.reduce((sum, coord) => sum + coord!.lat, 0) / availableCoords.length;
            const avgLng = availableCoords.reduce((sum, coord) => sum + coord!.lng, 0) / availableCoords.length;
            center = { lat: avgLat, lng: avgLng };
        }
        zoom = userZoom || 15;
    }

    // Create colored pin marker for public reports
    const createColoredPinMarker = (color: string) => {
        const pinSvg = `
            <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
                <!-- Pin shadow -->
                <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
                <!-- Pin body -->
                <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                      fill="${color}"
                      stroke="${color}"
                      stroke-width="1.5"/>
                <!-- Pin head circle -->
                <circle cx="18" cy="16" r="13" fill="${color}" stroke="${color}" stroke-width="2"/>
                <!-- White inner dot -->
                <circle cx="18" cy="16" r="5" fill="white"/>
            </svg>
        `;

        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(pinSvg),
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 44),
            origin: new window.google.maps.Point(0, 0)
        };
    };

    // Choose between icon-based or colored markers
    const googleMarkerIcon = useColoredMarkers
        ? createColoredPinMarker(googleMarkerColor)
        : {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
                    <!-- Pin shadow -->
                    <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
                    <!-- Pin body -->
                    <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                          fill="#ffffff"
                          stroke="#4285F5"
                          stroke-width="1.5"/>
                    <!-- Pin head circle -->
                    <circle cx="18" cy="16" r="13" fill="#ffffff" stroke="#4285F5" stroke-width="2"/>
                    <!-- Google Maps icon embedded (simplified) -->
                    <g transform="translate(7, 5) scale(0.7, 0.7)">
                        <circle cx="15" cy="15" r="11" fill="#ffffff"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M22 11.8C22 15 19.5 17.5 18 19.5C17.5 20 17 20.5 16.8 21C16.5 21.8 16.2 22.5 16 23C15.8 22.5 15.5 21.8 15.2 21C15 20.5 14.5 20 14 19.5C12.5 17.5 10 15 10 11.8C10 8 12.7 5 16 5C19.3 5 22 8 22 11.8ZM16 13.5C17.1 13.5 18 12.6 18 11.5C18 10.4 17.1 9.5 16 9.5C14.9 9.5 14 10.4 14 11.5C14 12.6 14.9 13.5 16 13.5Z" fill="#34A851"/>
                        <path d="M21.5 9C20.8 7.8 19.6 6.9 18 6.5L15 9.5C15.3 9.2 15.7 9 16 9C17.1 9 18 9.9 18 11C18 11.4 17.9 11.7 17.7 12L21.5 9Z" fill="#4285F5"/>
                        <path d="M14 11C14 10.6 14.1 10.3 14.3 10L11 13.5C11.5 14.5 12.2 15.5 13 16.5L17.7 12C17.5 12.3 17.1 12.5 16.7 12.5C15.6 12.5 14.7 11.6 14 11Z" fill="#F9BB0E"/>
                        <path d="M11 8C10.2 9 10 10.4 10 11.8C10 12.5 10.2 13.2 10.5 13.9L13.5 10.5L11 8Z" fill="#E74335"/>
                    </g>
                </svg>
            `),
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 44),
            origin: new window.google.maps.Point(0, 0)
        };

    const radarMarkerIcon = useColoredMarkers
        ? createColoredPinMarker(radarMarkerColor)
        : {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
                    <!-- Pin shadow -->
                    <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
                    <!-- Pin body -->
                    <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                          fill="#ffffff"
                          stroke="#000257"
                          stroke-width="1.5"/>
                    <!-- Pin head circle -->
                    <circle cx="18" cy="16" r="13" fill="#ffffff" stroke="#000257" stroke-width="2"/>
                    <!-- Radar icon embedded (triangle) -->
                    <g transform="translate(18, 16)">
                        <path d="M 0 -8 L -6 4 L 6 4 Z" fill="#000257"/>
                    </g>
                </svg>
            `),
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 44),
            origin: new window.google.maps.Point(0, 0)
        };

    const uberMarkerIcon = useColoredMarkers
        ? createColoredPinMarker(uberMarkerColor)
        : {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
                    <!-- Pin shadow -->
                    <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
                    <!-- Pin body -->
                    <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                          fill="#ffffff"
                          stroke="#000000"
                          stroke-width="1.5"/>
                    <!-- Pin head circle -->
                    <circle cx="18" cy="16" r="13" fill="#ffffff" stroke="#000000" stroke-width="2"/>
                    <!-- Uber icon embedded (U glyph) -->
                    <g transform="translate(18, 16)">
                        <path d="M -5 -7 h 3 v 7.5 c 0 1.4 0.8 2.2 2 2.2 s 2 -0.8 2 -2.2 V -7 h 3 v 7.6 c 0 3 -2 4.9 -5 4.9 s -5 -1.9 -5 -4.9 Z" fill="#000000"/>
                    </g>
                </svg>
            `),
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 44),
            origin: new window.google.maps.Point(0, 0)
        };

    // Create pin-style marker for Current location
    const currentPinSvg = `
        <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
            <!-- Pin shadow -->
            <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
            <!-- Pin body -->
            <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                  fill="#ffffff"
                  stroke="#fd7e14"
                  stroke-width="1.5"/>
            <!-- Pin head circle -->
            <circle cx="18" cy="16" r="13" fill="#ffffff" stroke="#fd7e14" stroke-width="2"/>
            <!-- Current location dot with pulse effect -->
            <circle cx="18" cy="16" r="6" fill="#fd7e14"/>
            <circle cx="18" cy="16" r="6" fill="#fd7e14" opacity="0.5">
                <animate attributeName="r" from="6" to="12" dur="1.5s" repeatCount="indefinite"/>
                <animate attributeName="opacity" from="0.5" to="0" dur="1.5s" repeatCount="indefinite"/>
            </circle>
        </svg>
    `;

    const currentMarkerIcon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(currentPinSvg),
        scaledSize: new window.google.maps.Size(36, 48),
        anchor: new window.google.maps.Point(18, 44), // Anchor at pin point
        origin: new window.google.maps.Point(0, 0)
    };

    // Create pin-style marker for POD locations
    const podPinSvg = `
        <svg width="36" height="48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48">
            <!-- Pin shadow -->
            <ellipse cx="18" cy="46" rx="8" ry="2" fill="#000" opacity="0.3"/>
            <!-- Pin body -->
            <path d="M 18 44 L 11 24 Q 11 24 11 18 A 7 7 0 0 1 25 18 Q 25 24 25 24 L 18 44 Z"
                  fill="#ffffff"
                  stroke="#6f42c1"
                  stroke-width="1.5"/>
            <!-- Pin head circle -->
            <circle cx="18" cy="16" r="13" fill="#ffffff" stroke="#6f42c1" stroke-width="2"/>
            <!-- POD icon (camera symbol) -->
            <g transform="translate(18, 16)">
                <rect x="-7" y="-4" width="14" height="10" rx="2" fill="#6f42c1"/>
                <circle cx="0" cy="1" r="3" fill="#ffffff"/>
                <circle cx="-4" cy="-4" r="1.5" fill="#6f42c1"/>
            </g>
        </svg>
    `;

    const podMarkerIcon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(podPinSvg),
        scaledSize: new window.google.maps.Size(36, 48),
        anchor: new window.google.maps.Point(18, 44), // Anchor at pin point
        origin: new window.google.maps.Point(0, 0)
    };

    const handleMapClick = (e: google.maps.MapMouseEvent) => {
        if (onCoordinateChange && e.latLng) {
            onCoordinateChange(e.latLng.lat(), e.latLng.lng());
        }
    };

    const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
        if (onCoordinateChange && e.latLng) {
            onCoordinateChange(e.latLng.lat(), e.latLng.lng());
        }
    };

    const handleOpenFullScreenPreview = () => {
        setShowFullScreenPreview(true);
    };

    const handleCloseFullScreenPreview = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setShowFullScreenPreview(false);
        setPreviewBlob(null);
        setPreviewUrl(null);
    };

    const handleScreenCapture = (): null => {
        // Use the native browser screenshot API if available
        if ((navigator as any).mediaDevices?.getDisplayMedia) {
            (navigator as any).mediaDevices
                .getDisplayMedia({
                    video: { mediaSource: 'screen' },
                    audio: false
                })
                .then((stream: MediaStream) => {
                    const video = document.createElement('video');
                    video.srcObject = stream;
                    video.play();

                    video.onloadedmetadata = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.drawImage(video, 0, 0);
                        }

                        // Stop the stream
                        stream.getTracks().forEach((track) => track.stop());

                        // Convert canvas to blob
                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    toast.error('Failed to create image');
                                    return;
                                }

                                const url = URL.createObjectURL(blob);
                                setPreviewBlob(blob);
                                setPreviewUrl(url);
                                toast.success('Screenshot captured successfully');
                            },
                            'image/png',
                            0.95
                        );
                    };
                })
                .catch((error: Error) => {
                    // User cancelled or error occurred
                    if (error.name !== 'NotAllowedError') {
                        toast.error(`Capture error: ${error.message}`);
                    }
                });
        } else {
            toast.error('Screen capture not supported in your browser');
        }
        return null;
    };

    const handleSavePOD = async () => {
        if (!previewBlob || !traceId) {
            toast.error('No image to upload');
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', previewBlob, `street-view-${Date.now()}.png`);

            await apiClient.post(`api/v1/address-pods/upload?traceId=${traceId}`, formData);

            toast.success('POD image uploaded successfully!');
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setShowFullScreenPreview(false);
            setPreviewBlob(null);
            setPreviewUrl(null);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                toast.error(`Upload failed: ${error.response.data || 'Unknown error'}`);
            } else {
                toast.error(`Upload error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className={hideControls ? "h-100" : "card shadow-sm"}>
            {!hideControls && (
                <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="fw-bold">Coordinate Comparison Map</div>
                        <div className="d-flex flex-column gap-2 small">
                            {googleCoordinates && (
                                <div className="d-flex align-items-center gap-2">
                                    {useColoredMarkers ? (
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: googleMarkerColor,
                                            border: '2px solid #fff',
                                            boxShadow: `0 0 0 1px ${googleMarkerColor}`
                                        }}></div>
                                    ) : (
                                        <img
                                            src={gmapsIcon}
                                            alt={googleLabel}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    )}
                                    <span>
                                        <strong>{googleLabel}:</strong> {googleCoordinates.lat.toFixed(6)}, {googleCoordinates.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {radarCoordinates && (
                                <div className="d-flex align-items-center gap-2">
                                    {useColoredMarkers ? (
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: radarMarkerColor,
                                            border: '2px solid #fff',
                                            boxShadow: `0 0 0 1px ${radarMarkerColor}`
                                        }}></div>
                                    ) : (
                                        <img
                                            src={radarIcon}
                                            alt={radarLabel}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    )}
                                    <span>
                                        <strong>{radarLabel}:</strong> {radarCoordinates.lat.toFixed(6)}, {radarCoordinates.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {uberCoordinates && (
                                <div className="d-flex align-items-center gap-2">
                                    {useColoredMarkers ? (
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            backgroundColor: uberMarkerColor,
                                            border: '2px solid #fff',
                                            boxShadow: `0 0 0 1px ${uberMarkerColor}`
                                        }}></div>
                                    ) : (
                                        <img
                                            src={uberIcon}
                                            alt={uberLabel}
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    )}
                                    <span>
                                        <strong>{uberLabel}:</strong> {uberCoordinates.lat.toFixed(6)}, {uberCoordinates.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {deliveredCoordinates && (
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: '#6f42c1',
                                        border: '2px solid #fff',
                                        boxShadow: '0 0 0 1px #6f42c1'
                                    }}></div>
                                    <span>
                                        <strong>Delivered:</strong> {deliveredCoordinates.lat.toFixed(6)}, {deliveredCoordinates.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {currentCoordinates && (
                                <div className="d-flex align-items-center gap-2">
                                    <div style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        backgroundColor: '#fd7e14',
                                        border: '2px solid #fff',
                                        boxShadow: '0 0 0 1px #fd7e14'
                                    }}></div>
                                    <span>
                                        <strong>Current:</strong> {currentCoordinates.lat.toFixed(6)}, {currentCoordinates.lng.toFixed(6)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Map Type Switcher */}
                    <div className="btn-group btn-group-sm w-100" role="group">
                        <button
                            type="button"
                            className={`btn ${mapType === 'roadmap' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setMapType('roadmap')}
                            title="Standard roadmap view"
                        >
                            <i className="bi bi-map me-1"></i>Road
                        </button>
                        <button
                            type="button"
                            className={`btn ${mapType === 'satellite' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setMapType('satellite')}
                            title="Satellite imagery"
                        >
                            <i className="bi bi-image me-1"></i>Satellite
                        </button>
                        <button
                            type="button"
                            className={`btn ${mapType === 'hybrid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setMapType('hybrid')}
                            title="Hybrid view with labels"
                        >
                            <i className="bi bi-layers me-1"></i>Hybrid
                        </button>
                        <button
                            type="button"
                            className={`btn ${mapType === 'terrain' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setMapType('terrain')}
                            title="Terrain view"
                        >
                            <i className="bi bi-diagram-3 me-1"></i>Terrain
                        </button>
                        <button
                            type="button"
                            className={`btn ${mapType === 'streetview' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setMapType('streetview')}
                            title="Street view perspective"
                        >
                            <i className="bi bi-eye me-1"></i>Street View
                        </button>
                    </div>
                </div>
            )}
            <div className={`card-body p-0 ${hideControls ? 'h-100' : ''}`}>
                <div style={{ height: height }} ref={mapContainerRef}>
                    {mapType === 'streetview' ? (
                        <iframe
                            title="Google Street View"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            src={`https://www.google.com/maps/embed/v1/streetview?location=${(currentCoordinates || radarCoordinates || googleCoordinates || center).lat},${(currentCoordinates || radarCoordinates || googleCoordinates || center).lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{
                                width: '100%',
                                height: '100%'
                            }}
                            center={center}
                            zoom={zoom}
                            options={{
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: false,
                                mapTypeId: mapType as google.maps.MapTypeId,
                                draggableCursor: onCoordinateChange ? 'pointer' : undefined
                            }}
                            onClick={handleMapClick}
                        >
                            {googleCoordinates && (
                                <MarkerF
                                    position={{
                                        lat: googleCoordinates.lat,
                                        lng: googleCoordinates.lng
                                    }}
                                    icon={googleMarkerIcon}
                                    title={`${googleLabel} Coordinates: ${googleCoordinates.lat}, ${googleCoordinates.lng}`}
                                />
                            )}
                            {radarCoordinates && (
                                <MarkerF
                                    position={{
                                        lat: radarCoordinates.lat,
                                        lng: radarCoordinates.lng
                                    }}
                                    icon={radarMarkerIcon}
                                    title={`${radarLabel} Coordinates: ${radarCoordinates.lat}, ${radarCoordinates.lng}`}
                                />
                            )}
                            {uberCoordinates && (
                                <MarkerF
                                    position={{
                                        lat: uberCoordinates.lat,
                                        lng: uberCoordinates.lng
                                    }}
                                    icon={uberMarkerIcon}
                                    title={`${uberLabel} Coordinates: ${uberCoordinates.lat}, ${uberCoordinates.lng}`}
                                />
                            )}
                            {deliveredCoordinates && (
                                <MarkerF
                                    position={{
                                        lat: deliveredCoordinates.lat,
                                        lng: deliveredCoordinates.lng
                                    }}
                                    icon={podMarkerIcon}
                                    title={`Delivered Coordinates: ${deliveredCoordinates.lat}, ${deliveredCoordinates.lng}`}
                                />
                            )}
                            {currentCoordinates && (
                                <MarkerF
                                    position={{
                                        lat: currentCoordinates.lat,
                                        lng: currentCoordinates.lng
                                    }}
                                    icon={currentMarkerIcon}
                                    title={`Current Coordinates: ${currentCoordinates.lat}, ${currentCoordinates.lng}`}
                                    draggable={!!onCoordinateChange}
                                    onDragEnd={handleMarkerDragEnd}
                                />
                            )}
                            {podMarkers && podMarkers.length > 0 && podMarkers.map((pod) => (
                                <MarkerF
                                    key={`pod-${pod.id}`}
                                    position={{
                                        lat: pod.lat,
                                        lng: pod.lng
                                    }}
                                    icon={podMarkerIcon}
                                    title={`POD #${pod.id}${pod.trackingNumber ? ` - Tracking: ${pod.trackingNumber}` : ''}\nCreated: ${new Date(pod.createdAt).toLocaleString()}`}
                                />
                            ))}
                        </GoogleMap>
                    )}
                </div>
                {!hideControls && mapType === 'streetview' && traceId && (
                    <div className="card-footer bg-light d-flex justify-content-between align-items-center">
                        <div className="small text-muted">
                            <i className="bi bi-info-circle me-1"></i>
                            Capture and upload POD images from Street View
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                            onClick={handleOpenFullScreenPreview}
                            disabled={isUploading}
                        >
                            <i className="bi bi-fullscreen"></i>
                            Preview
                        </button>
                    </div>
                )}
                {/* <div className="card-footer bg-light small text-muted">
                    {address && <div className="fw-medium text-dark mb-1">{address}</div>}
                    {currentCoordinates && (
                        <div>
                            <strong className="text-warning">Current:</strong> {currentCoordinates.lat}, {currentCoordinates.lng}
                        </div>
                    )}
                </div> */}
            </div>

            {/* Full Screen Preview Modal */}
            {showFullScreenPreview && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: '#f8f9fa',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: '15px 20px',
                            backgroundColor: '#fff',
                            borderBottom: '1px solid #dee2e6',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <h5 style={{ margin: 0 }}>Capture Street View</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={handleCloseFullScreenPreview}
                            disabled={isUploading}
                        ></button>
                    </div>

                    {/* Main Content - Map and Buttons */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {showCropMode && previewUrl ? (
                            <>
                                {/* Crop Mode */}
                                <div style={{ flex: 1, position: 'relative', backgroundColor: '#000' }}>
                                    <Cropper
                                        image={previewUrl}
                                        crop={cropPosition}
                                        zoom={cropZoom}
                                        aspect={16 / 9}
                                        onCropChange={setCropPosition}
                                        onCropAreaChange={handleOnCropComplete}
                                        onZoomChange={setCropZoom}
                                    />
                                </div>

                                {/* Crop Controls */}
                                <div
                                    style={{
                                        padding: '15px 20px',
                                        backgroundColor: '#fff',
                                        borderTop: '1px solid #dee2e6',
                                        display: 'flex',
                                        gap: '10px',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{ fontSize: '12px', color: '#666', flex: 1 }}>
                                        Adjust the crop area and zoom to frame your image
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setShowCropMode(false)}
                                        disabled={isUploading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                                        onClick={createCroppedImage}
                                        disabled={isUploading}
                                    >
                                        <i className="bi bi-check-lg"></i>
                                        Apply Crop
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Map Container for Capture */}
                                <div
                                    ref={fullScreenMapRef}
                                    style={{
                                        flex: 1,
                                        backgroundColor: '#fff',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <iframe
                                        title="Google Street View"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 'none' }}
                                        src={`https://www.google.com/maps/embed/v1/streetview?location=${(currentCoordinates || radarCoordinates || googleCoordinates || { lat: 39.8283, lng: -98.5795 }).lat},${(currentCoordinates || radarCoordinates || googleCoordinates || { lat: 39.8283, lng: -98.5795 }).lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
                                        allowFullScreen
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    />
                                </div>

                                {/* Image Preview or Capture Controls */}
                                {previewUrl ? (
                                    <div
                                        style={{
                                            flex: 1,
                                            backgroundColor: '#f8f9fa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'auto'
                                        }}
                                    >
                                        <img
                                            src={previewUrl}
                                            alt="Captured preview"
                                            style={{
                                                maxWidth: '90%',
                                                maxHeight: '90%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </div>
                                ) : null}

                                {/* Control Bar */}
                                <div
                                    style={{
                                        padding: '15px 20px',
                                        backgroundColor: '#fff',
                                        borderTop: '1px solid #dee2e6',
                                        display: 'flex',
                                        gap: '10px',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center'
                                    }}
                                >
                                    {previewBlob && (
                                        <>
                                            <span style={{ fontSize: '14px', color: '#666' }}>
                                                Image captured <i className="bi bi-check-circle-fill text-success ms-1"></i>
                                            </span>
                                            <div style={{ flex: 1 }}></div>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-primary d-flex align-items-center gap-2"
                                        onClick={handleScreenCapture}
                                        disabled={isUploading}
                                    >
                                        <i className="bi bi-camera-fill"></i>
                                        Capture
                                    </button>

                                    {previewBlob && (
                                        <button
                                            type="button"
                                            className="btn btn-warning d-flex align-items-center gap-2"
                                            onClick={() => setShowCropMode(true)}
                                            disabled={isUploading}
                                        >
                                            <i className="bi bi-crop"></i>
                                            Crop
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCloseFullScreenPreview}
                                        disabled={isUploading || !previewBlob}
                                    >
                                        Close
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-success d-flex align-items-center gap-2"
                                        onClick={handleSavePOD}
                                        disabled={isUploading || !previewBlob}
                                    >
                                        {isUploading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-cloud-upload"></i>
                                                Save POD
                                            </>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                </div>
            )}
        </div>
    );
};

export default ComparisonMap;